'use client';


/** Imports */
import classNames from 'classnames';
import styles from './dialog.module.css';
import {useTranslations} from 'next-intl';
import {MdOutlineClose} from 'react-icons/md';
import Confetti from '@/components/Confetti/Confetti';
import React, {useEffect, useId, useState, useRef} from 'react';


/** Types */
type Props = Readonly<{
    reference: React.RefObject<HTMLDialogElement | null>;
    headline: string | React.ReactNode;
    subHeadline?: string | React.ReactNode;
    children?: React.ReactNode;
    disableCloseOnBackdrop?: boolean;
    disableManualScrolling?: boolean;
    foot?: React.ReactNode;
    extraClasses?: Array<string>;
    noCloseButton?: boolean;
    closeButtonLabel?: string;
    startOpen?: boolean;
    confetti?: boolean;
    onClose?: () => void;
    onRequestClose?: () => void;
    onOpen?: () => void;
}>;


/** Component */
export default function Dialog(props: Props) {
    const {
        reference, headline, subHeadline, children, disableCloseOnBackdrop, disableManualScrolling,
        foot, extraClasses, noCloseButton, closeButtonLabel, startOpen, confetti, onClose, onOpen, onRequestClose,
    } = props;

    // Hooks
    const tAria = useTranslations('common.ariaLabels');


    // Refs
    const confettiRef = useRef<HTMLDialogElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const headlineId = useId();
    const subHeadlineId = useId();

    // State
    const [isSticky, setIsSticky] = useState(false);

    // Effects
    useEffect(() => {
        if (startOpen) reference.current?.showModal();
    }, [startOpen, reference]);
    useEffect(() => {
        if (!scrollRef.current) return;
        const abortController = new AbortController();
        if (disableManualScrolling) {
            scrollRef.current.addEventListener('touchmove', preventDefaultHandler, {
                passive: false,
                signal: abortController.signal,
            });
            scrollRef.current.addEventListener('wheel', preventDefaultHandler, {
                passive: false,
                signal: abortController.signal,
            });
        } else {
            scrollRef.current.removeEventListener('touchmove', preventDefaultHandler);
            scrollRef.current.removeEventListener('wheel', preventDefaultHandler);
        }

        function preventDefaultHandler(e: Event) {
            e.preventDefault();
        }

        // Cleanup
        return () => abortController.abort();
    }, [disableManualScrolling]);


    // React currently attaches dialog toggle listeners on client mount but not hydration.
    // A native listener keeps open/close callbacks working for server-rendered dialogs too.
    useEffect(() => {
        const dialog = reference.current;
        if (!dialog) return;
        function handleToggle() {
            if (dialog?.open) {
                onOpen?.();
                confettiRef.current?.show();
            } else {
                onClose?.();
                confettiRef.current?.close();
            }
        }
        dialog.addEventListener('toggle', handleToggle);
        return () => dialog.removeEventListener('toggle', handleToggle);
    }, [reference, onOpen, onClose]);


    // Event handlers
    /** Delegate guarded closure to the caller, falling back to the native dialog close behavior. */
    function requestClose() {
        if (onRequestClose) onRequestClose();
        else reference.current?.close();
    }

    function handleScroll(event: React.UIEvent<HTMLDivElement>) {
        const scrollTop = event.currentTarget.scrollTop;
        setIsSticky(scrollTop > 0);
    }




    // Render
    return (<>

        {/* Dialog */}
        <dialog
            ref={reference}
            className={classNames(styles.dialog, ...(extraClasses || []))}
            aria-labelledby={headlineId}
            aria-describedby={
                (subHeadline)
                    ? subHeadlineId
                    : undefined
            }
            onClick={(event) => !disableCloseOnBackdrop
                && (event.target === reference.current)
                && requestClose()}
            onCancel={(event) => {
                if (noCloseButton || onRequestClose) event.preventDefault();
                if (!noCloseButton && onRequestClose) onRequestClose();
            }}
        >
            <div
                ref={scrollRef}
                className={classNames(
                    styles.scrollWrapper,
                    'dialog-scroll-wrapper',
                    {'custom-scrollbar': !disableManualScrolling},
                    {'no-scrollbar': disableManualScrolling},
                )}
                onScroll={handleScroll}
            >
                <div className={classNames(styles.head, 'dialog-head', {[styles.sticky]: isSticky})}>
                    <div id={headlineId} className={styles.headline}>
                        {headline}
                    </div>
                    {subHeadline && (
                        <div id={subHeadlineId} className={styles.subHeadline}>
                            {subHeadline}
                        </div>
                    )}
                    {!noCloseButton && (
                        <button
                            type="button"
                            className={classNames(styles.closeButton, 'dialog-close')}
                            onClick={requestClose}
                            aria-label={closeButtonLabel ?? tAria('closeDialog')}
                        >
                            <MdOutlineClose aria-hidden="true"/>
                        </button>
                    )}
                </div>
                <div className={classNames(styles.body, 'dialog-body')}>
                    {children}
                </div>
            </div>
            {foot && (
                <div className={classNames(styles.foot, 'dialog-foot')}>
                    {foot}
                </div>
            )}
        </dialog>

        {/* Confetti */}
        {confetti && (
            <dialog ref={confettiRef} className={styles.confettiDialog}>
                <Confetti/>
            </dialog>
        )}
    </>);
}
